/**
 * Article archive across every blog (sections/bst-article-archive.liquid).
 *
 * Liquid can only paginate / tag-filter inside a single blog, so this fetches
 * every published article once from the Storefront API (tokenless access
 * covers blogs and articles), then filters by tag and paginates in the
 * browser. State lives in the URL (?tag=<handle>&page=<n>) so filtered pages
 * are shareable and the back button works. The fetched list is cached in
 * sessionStorage for the browsing session.
 */

const API_VERSION = '2026-07';
const CACHE_KEY = 'bst-article-archive:v1';
const IMAGES_CACHE_KEY = 'bst-article-archive:images:v1';
const CACHE_TTL_MS = 10 * 60 * 1000;

const ARTICLES_QUERY = `
  query BstArticleArchive($cursor: String) {
    articles(first: 250, after: $cursor, sortKey: PUBLISHED_AT, reverse: true) {
      nodes {
        id
        title
        onlineStoreUrl
        publishedAt
        excerpt
        content(truncateAt: 240)
        tags
        image { url altText width height }
        blog { handle title }
      }
      pageInfo { hasNextPage endCursor }
    }
  }
`;

/** @param {string} value */
const toHandle = (value) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/** @param {any} article */
const articlePath = (article) => new URL(article.onlineStoreUrl).pathname;

/**
 * custom.custom_image_desktop / custom_image_mobile per article path. The Storefront
 * API's tokenless access can't read metafields, so they come from each article's
 * templates/article.bst-image-data.liquid view - fetched only for the articles being
 * shown, and kept for the browsing session.
 * @type {Map<string, { desktop: any, mobile: any }>}
 */
const customImages = new Map();
try {
  const cached = JSON.parse(sessionStorage.getItem(IMAGES_CACHE_KEY) || 'null');
  if (cached && Date.now() - cached.savedAt < CACHE_TTL_MS) {
    for (const [path, images] of Object.entries(cached.images)) customImages.set(path, images);
  }
} catch {
  // Storage unavailable - just fetch.
}

/** @param {any[]} articles */
async function loadCustomImages(articles) {
  const missing = articles.map(articlePath).filter((path) => !customImages.has(path));
  if (!missing.length) return;

  await Promise.all(
    missing.map(async (path) => {
      try {
        const response = await fetch(`${path}?view=bst-image-data`);
        if (!response.ok) throw new Error(String(response.status));
        customImages.set(path, await response.json());
      } catch {
        // Not cached, so the next render retries; until then the featured image is used.
      }
    })
  );

  try {
    sessionStorage.setItem(
      IMAGES_CACHE_KEY,
      JSON.stringify({ savedAt: Date.now(), images: Object.fromEntries(customImages) })
    );
  } catch {
    // Quota/storage errors only cost a refetch next time.
  }
}

/**
 * Same fallback order as sections/bst-article-body-b.liquid: the custom metafields
 * first, then the article's featured image.
 * @param {any} article
 */
const articleImages = (article) => {
  const custom = customImages.get(articlePath(article));
  const fallback = article.image?.url ? article.image : null;
  return {
    desktop: custom?.desktop || fallback,
    mobile: custom?.mobile || custom?.desktop || fallback,
  };
};

const dateFormatter =new Intl.DateTimeFormat('en-US', { month: 'long', day: '2-digit', year: 'numeric' });

async function fetchAllArticles() {
  try {
    const cached = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
    if (cached && Date.now() - cached.savedAt < CACHE_TTL_MS) return cached.articles;
  } catch {
    // Storage unavailable (private mode, blocked) - just fetch.
  }

  const articles = [];
  let cursor = null;

  do {
    const response = await fetch(`/api/${API_VERSION}/graphql.json`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ query: ARTICLES_QUERY, variables: { cursor } }),
    });
    if (!response.ok) throw new Error(`Storefront API responded ${response.status}`);

    const { data, errors } = await response.json();
    if (errors?.length) throw new Error(errors.map((error) => error.message).join('; '));

    articles.push(...data.articles.nodes);
    cursor = data.articles.pageInfo.hasNextPage ? data.articles.pageInfo.endCursor : null;
  } while (cursor);

  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ savedAt: Date.now(), articles }));
  } catch {
    // Quota/storage errors only cost a refetch next time.
  }

  return articles;
}

class BstArticleArchive extends HTMLElement {
  /** @type {any[]} */
  #articles = [];
  /** @type {{ label: string, handle: string, count: number }[]} */
  #tags = [];
  #renderId = 0;

  connectedCallback() {
    this.perPage = Number(this.dataset.postsPerPage) || 12;
    this.showParentBlog = this.dataset.showParentBlog === 'true';
    this.defaultRatio = this.dataset.defaultRatio || 'adapt';
    this.desktopRatio = this.dataset.desktopRatio || 'adapt';
    this.featuredLayout = this.dataset.featuredLayout || 'none';
    this.excludedBlogs = (this.dataset.excludedBlogs || '')
      .split(',')
      .map((handle) => handle.trim().toLowerCase())
      .filter(Boolean);

    this.grid = this.querySelector('[data-grid]');
    this.top = this.querySelector('[data-top]');
    this.tagsContainer = this.querySelector('[data-tags]');
    this.paginationContainer = this.querySelector('[data-pagination]');
    this.status = this.querySelector('[data-status]');
    this.emptyMessage = this.querySelector('[data-empty]');
    this.errorMessage = this.querySelector('[data-error]');

    this.addEventListener('click', this.#onClick);
    window.addEventListener('popstate', this.#onPopState);

    this.#load();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    window.removeEventListener('popstate', this.#onPopState);
  }

  async #load() {
    try {
      const articles = await fetchAllArticles();
      this.#articles = articles.filter(
        (article) => article.onlineStoreUrl && !this.excludedBlogs.includes(article.blog.handle.toLowerCase())
      );
    } catch (error) {
      console.error('[bst-article-archive]', error);
      this.grid.replaceChildren();
      this.errorMessage.hidden = false;
      return;
    }

    this.#tags = this.#collectTags();
    this.#render({ scroll: false });
  }

  /** Every tag in use, most-used first - new tags show up as soon as an article has them. */
  #collectTags() {
    /** @type {Map<string, { label: string, handle: string, count: number }>} */
    const byHandle = new Map();
    for (const article of this.#articles) {
      for (const label of article.tags) {
        const handle = toHandle(label);
        if (!handle) continue;
        const entry = byHandle.get(handle) || { label, handle, count: 0 };
        entry.count += 1;
        byHandle.set(handle, entry);
      }
    }
    return [...byHandle.values()].sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  }

  #readState() {
    const params = new URLSearchParams(window.location.search);
    const tag = params.get('tag') || '';
    const page = Math.max(1, parseInt(params.get('page') || '1', 10) || 1);
    return { tag: this.#tags.some((entry) => entry.handle === tag) ? tag : '', page };
  }

  /** @param {{ tag: string, page: number }} state */
  #urlFor({ tag, page }) {
    const url = new URL(window.location.href);
    if (tag) url.searchParams.set('tag', tag);
    else url.searchParams.delete('tag');
    if (page > 1) url.searchParams.set('page', String(page));
    else url.searchParams.delete('page');
    return url;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const tagButton = event.target.closest('[data-tag]');
    if (tagButton instanceof HTMLElement) {
      this.#navigate({ tag: tagButton.dataset.tag || '', page: 1 });
      return;
    }

    const pageLink = event.target.closest('[data-page]');
    if (pageLink instanceof HTMLElement && !event.metaKey && !event.ctrlKey && !event.shiftKey) {
      event.preventDefault();
      this.#navigate({ tag: this.#readState().tag, page: Number(pageLink.dataset.page) });
    }
  };

  #onPopState = () => {
    if (this.#articles.length) this.#render({ scroll: false });
  };

  /** @param {{ tag: string, page: number }} state */
  #navigate(state) {
    window.history.pushState({}, '', this.#urlFor(state));
    this.#render({ scroll: true });
  }

  /** @param {{ scroll: boolean }} options */
  async #render({ scroll }) {
    const renderId = ++this.#renderId;
    const { tag, page: requestedPage } = this.#readState();
    const filtered = tag
      ? this.#articles.filter((article) => article.tags.some((label) => toHandle(label) === tag))
      : this.#articles;

    const totalPages = Math.max(1, Math.ceil(filtered.length / this.perPage));
    const page = Math.min(requestedPage, totalPages);
    const pageItems = filtered.slice((page - 1) * this.perPage, page * this.perPage);

    // Wait for this page's metafield images so cards never swap images after appearing.
    this.setAttribute('aria-busy', 'true');
    await loadCustomImages(pageItems);
    // A later tag/page click started its own render while this one was waiting.
    if (renderId !== this.#renderId) return;
    this.removeAttribute('aria-busy');

    this.#renderTags(tag);

    // Page 1 of every filter (including each tag) leads with that filter's newest
    // post as the featured post, like The Journal - split also pulls the next 4
    // into a 2x2 grid beside it. The page still holds `perPage` posts in total.
    let gridItems = pageItems;
    if (page === 1 && this.featuredLayout !== 'none' && pageItems.length) {
      // A split hero beside an empty 2x2 grid looks broken, so a filter with a
      // single post falls back to the full-width banner.
      const split = this.featuredLayout === 'split' && pageItems.length > 1;
      const [featured, ...rest] = pageItems;
      const topGridItems = split ? rest.slice(0, 4) : [];
      gridItems = rest.slice(topGridItems.length);
      this.top.replaceChildren(this.#renderFeatured(featured, topGridItems, split));
    } else {
      this.top.replaceChildren();
    }

    this.grid.replaceChildren(...gridItems.map((article, index) => this.#renderCard(article, index + 5)));
    this.grid.hidden = gridItems.length === 0;
    this.emptyMessage.hidden = filtered.length > 0;
    this.#renderPagination(page, totalPages, tag);

    const tagLabel = this.#tags.find((entry) => entry.handle === tag)?.label;
    this.status.textContent = `${filtered.length} ${filtered.length === 1 ? 'story' : 'stories'}${
      tagLabel ? ` tagged ${tagLabel}` : ''
    }${totalPages > 1 ? `, page ${page} of ${totalPages}` : ''}`;

    if (scroll) {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.scrollIntoView({ behavior: reduceMotion ? 'instant' : 'smooth', block: 'start' });
    }
  }

  /** @param {string} activeTag */
  #renderTags(activeTag) {
    if (!this.tagsContainer) return;
    if (!this.#tags.length) {
      this.tagsContainer.hidden = true;
      return;
    }

    const buttons = [{ label: this.dataset.labelAll || 'All', handle: '' }, ...this.#tags].map(({ label, handle }) => {
      const button = document.createElement('button');
      const active = handle === activeTag;
      button.type = 'button';
      button.className = `bst-blog-post-list__tag${active ? ' bst-blog-post-list__tag--active' : ''}`;
      button.dataset.tag = handle;
      button.setAttribute('aria-pressed', String(active));
      button.textContent = label;
      return button;
    });

    this.tagsContainer.replaceChildren(...buttons);
    this.tagsContainer.hidden = false;

    // On mobile the tags are a horizontal scroller - keep the active pill in
    // view (e.g. a shared ?tag= link to a pill past the screen edge). Set
    // scrollLeft directly rather than scrollIntoView so the page itself never
    // scrolls vertically.
    const container = this.tagsContainer;
    const activeButton = buttons.find((button) => button.getAttribute('aria-pressed') === 'true');
    if (activeButton && container.scrollWidth > container.clientWidth) {
      const padding = parseFloat(getComputedStyle(container).paddingInlineStart) || 0;
      const left = activeButton.offsetLeft - container.offsetLeft;
      const right = left + activeButton.offsetWidth;
      if (left - padding < container.scrollLeft || right > container.scrollLeft + container.clientWidth) {
        container.scrollLeft = left - padding;
      }
    }
  }

  /**
   * @param {any} featured
   * @param {any[]} topGridItems
   * @param {boolean} split
   */
  #renderFeatured(featured, topGridItems, split) {
    const template = /** @type {HTMLTemplateElement} */ (this.querySelector('[data-featured-template]'));
    const fragment = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
    const top = /** @type {HTMLElement} */ (fragment.firstElementChild);
    const hero = top.querySelector('.bst-blog-post-list__featured');

    top.classList.toggle('bst-blog-post-list__top--split', split);
    hero.classList.toggle('bst-blog-post-list__featured--split', split);

    const media = hero.querySelector('[data-media]');
    const image = this.#imageElement(featured, {
      className: 'bst-blog-post-list__featured-image',
      widths: [750, 1100, 1400, 2000],
      sizes: '100vw',
      eager: true,
    });
    media.prepend(image || this.#placeholder('bst-blog-post-list__featured-image'));

    this.#fillText(hero, featured);

    if (split) {
      const topGrid = document.createElement('div');
      topGrid.className = 'bst-blog-post-list__top-grid';
      topGrid.append(...topGridItems.map((article, index) => this.#renderCard(article, index + 1, '300 / 205')));
      top.append(topGrid);
    }

    return top;
  }

  /**
   * Shared by the featured post and the cards - their markup uses the same data hooks.
   * @param {HTMLElement} root
   * @param {any} article
   */
  #fillText(root, article) {
    const eyebrow = root.querySelector('[data-eyebrow]');
    if (this.showParentBlog) eyebrow.textContent = article.blog.title.replace('The', '').trim();
    else eyebrow.remove();

    root.querySelector('[data-title]').textContent = article.title;

    const excerpt = (article.excerpt || article.content || '').trim();
    const excerptElement = root.querySelector('[data-excerpt]');
    if (excerpt) excerptElement.textContent = excerpt;
    else excerptElement.remove();

    root.querySelector('[data-date]').textContent = dateFormatter.format(new Date(article.publishedAt));

    const link = /** @type {HTMLAnchorElement} */ (root.querySelector('[data-link]'));
    link.href = new URL(article.onlineStoreUrl).pathname;
    root.querySelector('[data-link-label]').textContent = article.title;
  }

  /**
   * @param {any} article
   * @param {{ className: string, widths: number[], sizes: string, eager: boolean }} options
   */
  #imageElement(article, { className, widths, sizes, eager }) {
    const { desktop, mobile } = articleImages(article);
    if (!desktop) return null;

    /** @param {any} image @param {number} width */
    const widthUrl = (image, width) => {
      const url = new URL(image.url, window.location.href);
      url.searchParams.set('width', String(width));
      return url.toString();
    };
    /** @param {any} image */
    const srcsetFor = (image) => widths.map((width) => `${widthUrl(image, width)} ${width}w`).join(', ');

    // Mirrors the Liquid sections' <picture>: mobile image as a <source>, desktop on the <img>.
    const picture = document.createElement('picture');
    if (mobile && mobile.url !== desktop.url) {
      const source = document.createElement('source');
      source.media = '(max-width: 749px)';
      source.srcset = srcsetFor(mobile);
      source.sizes = '100vw';
      if (mobile.width && mobile.height) {
        source.width = mobile.width;
        source.height = mobile.height;
      }
      picture.append(source);
    }

    const img = document.createElement('img');
    img.className = className;
    img.srcset = srcsetFor(desktop);
    img.sizes = sizes;
    img.src = widthUrl(desktop, widths[widths.length - 1]);
    img.alt = desktop.altText || article.title;
    img.loading = eager ? 'eager' : 'lazy';
    if (desktop.width && desktop.height) {
      img.width = desktop.width;
      img.height = desktop.height;
    }
    picture.append(img);
    return picture;
  }

  /** @param {string} className */
  #placeholder(className) {
    const template = /** @type {HTMLTemplateElement} */ (this.querySelector('[data-placeholder-template]'));
    const fragment = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
    fragment.firstElementChild?.setAttribute('class', className);
    return fragment;
  }

  /**
   * @param {any} article
   * @param {number} index
   * @param {string} [ratioOverride] - fixed ratio (the split layout's 2x2 grid uses 300 / 205)
   */
  #renderCard(article, index, ratioOverride) {
    const template = /** @type {HTMLTemplateElement} */ (this.querySelector('[data-card-template]'));
    const fragment = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
    const card = /** @type {HTMLElement} */ (fragment.firstElementChild);
    const image = articleImages(article).desktop;

    const imageRatio = image?.width && image?.height ? image.width / image.height : 1.523;
    const ratio = ratioOverride || (this.defaultRatio === 'adapt' ? imageRatio : this.defaultRatio);
    card.style.setProperty('--bst-blog-post-list-ratio', String(ratio));
    if (!ratioOverride) {
      const desktopRatio = this.desktopRatio === 'adapt' ? imageRatio : this.desktopRatio;
      card.style.setProperty('--bst-article-archive-ratio-desktop', String(desktopRatio));
    }

    const media = card.querySelector('[data-media]');
    const img = this.#imageElement(article, {
      className: 'bst-blog-post-list__image',
      widths: [375, 750, 800],
      sizes: '(min-width: 750px) 420px, 100vw',
      eager: index < 3,
    });
    media.append(img || this.#placeholder('bst-blog-post-list__image'));

    this.#fillText(card, article);

    return card;
  }

  /**
   * Mirrors snippets/pagination-controls.liquid's markup/classes: prev, 1, …,
   * current ±1, …, last, next.
   * @param {number} page
   * @param {number} totalPages
   * @param {string} tag
   */
  #renderPagination(page, totalPages, tag) {
    if (totalPages <= 1) {
      this.paginationContainer.replaceChildren();
      return;
    }

    const visible = [...new Set([1, page - 1, page, page + 1, totalPages])]
      .filter((number) => number >= 1 && number <= totalPages)
      .sort((a, b) => a - b);

    const iconMarkup = (selector) =>
      /** @type {HTMLTemplateElement} */ (this.querySelector(selector)).innerHTML;

    const linkItem = (number, { label, html, className }) => {
      const href = this.#urlFor({ tag, page: number });
      return `<li class="pagination__item"><a href="${href.pathname}${href.search}" data-page="${number}" aria-label="${label}" class="pagination__link ${className}">${html}</a></li>`;
    };
    const disabledArrow = (label, html) =>
      `<li class="pagination__item"><span role="link" aria-label="${label}" aria-disabled="true" class="pagination__link pagination__link--arrow pagination__link--disabled">${html}</span></li>`;

    let items =
      page > 1
        ? linkItem(page - 1, { label: 'Previous page', html: iconMarkup('[data-prev-icon]'), className: 'pagination__link--arrow' })
        : disabledArrow('Previous page', iconMarkup('[data-prev-icon]'));

    let previous = 0;
    for (const number of visible) {
      if (number - previous > 1) {
        items += '<li class="pagination__item pagination__item--gap"><span class="pagination__link pagination__link--gap" aria-hidden="true">&hellip;</span></li>';
      }
      items +=
        number === page
          ? `<li class="pagination__item"><span class="pagination__link pagination__link--page pagination__link--current" aria-current="page">${number}</span></li>`
          : linkItem(number, { label: `Page ${number}`, html: String(number), className: 'pagination__link--page' });
      previous = number;
    }

    items +=
      page < totalPages
        ? linkItem(page + 1, { label: 'Next page', html: iconMarkup('[data-next-icon]'), className: 'pagination__link--arrow' })
        : disabledArrow('Next page', iconMarkup('[data-next-icon]'));

    this.paginationContainer.innerHTML = `<nav class="pagination" aria-label="Pagination"><ul class="pagination__list" role="list">${items}</ul></nav>`;
  }
}

if (!customElements.get('bst-article-archive')) {
  customElements.define('bst-article-archive', BstArticleArchive);
}
